import React from "react";

export type ClickAwayMouseEvent = "onClick" | "onMouseDown" | "onMouseUp" | false;
export type ClickAwayTouchEvent = "onTouchStart" | "onTouchEnd" | false;

export interface ClickAwayListenerProps {
  // Único filho válido. Recebe a ref para detectar cliques fora dele.
  children: React.ReactElement;
  // Se true, considera a árvore React (via portais) como "dentro", não disparando onClickAway.
  disableReactTree?: boolean;
  // Evento de mouse usado para detectar o clique fora (ou false para desativar).
  mouseEvent?: ClickAwayMouseEvent;
  // Evento de toque usado para detectar o clique fora (ou false para desativar).
  touchEvent?: ClickAwayTouchEvent;
  // Disparado quando um clique/toque ocorre fora do elemento filho.
  onClickAway: (event: MouseEvent | TouchEvent) => void;
}

const mapEventPropToEvent = (
  eventProp: Exclude<ClickAwayMouseEvent | ClickAwayTouchEvent, false>
): "click" | "mousedown" | "mouseup" | "touchstart" | "touchend" => {
  switch (eventProp) {
  case "onClick": return "click";
  case "onMouseDown": return "mousedown";
  case "onMouseUp": return "mouseup";
  case "onTouchStart": return "touchstart";
  case "onTouchEnd": return "touchend";
  }
};

// Equivalente ao ClickAwayListener: detecta cliques/toques
// fora do elemento filho e chama onClickAway. Não renderiza DOM próprio — apenas
// anexa uma ref ao filho e registra listeners no document.
const ClickAwayListener: React.FC<ClickAwayListenerProps> = (props) => {
  const {
    children,
    disableReactTree = false,
    mouseEvent = "onClick",
    touchEvent = "onTouchEnd",
    onClickAway,
  } = props;

  const nodeRef = React.useRef<Element | null>(null);
  // Evita que um evento de sincronização (ex.: foco/abertura) dispare o clique fora.
  const activatedRef = React.useRef(false);
  // Marca quando o evento se originou dentro da árvore React do filho (portais).
  const syntheticEventRef = React.useRef(false);

  React.useEffect(() => {
    // Pequeno atraso para ignorar o evento que montou o componente.
    const timeout = setTimeout(() => {
      activatedRef.current = true;
    }, 0);

    return () => {
      clearTimeout(timeout);
      activatedRef.current = false;
    };
  }, []);

  // Mantém a ref do filho preservando a ref original que ele já possa ter.
  const handleRef = React.useCallback((instance: Element | null) => {
    nodeRef.current = instance;

    const { ref } = children as unknown as { ref?: React.Ref<Element>; };

    if (typeof ref === "function") {
      ref(instance);
    } else if (ref && typeof ref === "object") {
      (ref as React.MutableRefObject<Element | null>).current = instance;
    }
  }, [children]);

  const handleClickAway = React.useCallback((event: MouseEvent | TouchEvent) => {
    // Captura o estado do sync flag e reseta para o próximo evento.
    const insideReactTree = syntheticEventRef.current;

    syntheticEventRef.current = false;

    const doc = nodeRef.current?.ownerDocument ?? document;

    // Ignora cliques com botão diferente do esquerdo.
    if (event instanceof MouseEvent && event.button !== undefined && event.button !== 0) {
      return;
    }

    if (!activatedRef.current || !nodeRef.current) {
      return;
    }

    // Determina se o clique ocorreu dentro do elemento filho.
    let insideDOM: boolean;

    if ("composedPath" in event && typeof event.composedPath === "function") {
      insideDOM = event.composedPath().indexOf(nodeRef.current) > -1;
    } else {
      insideDOM = !doc.documentElement.contains(event.target as Node | null)
        || nodeRef.current.contains(event.target as Node | null);
    }

    if (!insideDOM && (disableReactTree || !insideReactTree)) {
      onClickAway(event);
    }
  }, [disableReactTree, onClickAway]);

  // Cria handlers que apenas marcam que o evento veio de dentro da árvore React.
  const createHandleSynthetic = (handlerName: string) => (event: React.SyntheticEvent) => {
    syntheticEventRef.current = true;

    const childrenProps = children.props as Record<string, unknown>;
    const childrenPropsHandler = childrenProps[handlerName];

    if (typeof childrenPropsHandler === "function") {
      (childrenPropsHandler as (e: React.SyntheticEvent) => void)(event);
    }
  };

  const childrenProps: Record<string, unknown> = { ref: handleRef };

  if (touchEvent !== false) {
    childrenProps[touchEvent] = createHandleSynthetic(touchEvent);
  }

  if (mouseEvent !== false) {
    childrenProps[mouseEvent] = createHandleSynthetic(mouseEvent);
  }

  React.useEffect(() => {
    if (touchEvent !== false) {
      const mappedTouchEvent = mapEventPropToEvent(touchEvent);
      const doc = nodeRef.current?.ownerDocument ?? document;

      doc.addEventListener(mappedTouchEvent, handleClickAway);

      return () => {
        doc.removeEventListener(mappedTouchEvent, handleClickAway);
      };
    }

    return undefined;
  }, [handleClickAway, touchEvent]);

  React.useEffect(() => {
    if (mouseEvent !== false) {
      const mappedMouseEvent = mapEventPropToEvent(mouseEvent);
      const doc = nodeRef.current?.ownerDocument ?? document;

      doc.addEventListener(mappedMouseEvent, handleClickAway);

      return () => {
        doc.removeEventListener(mappedMouseEvent, handleClickAway);
      };
    }

    return undefined;
  }, [handleClickAway, mouseEvent]);

  return React.cloneElement(children, childrenProps);
};

export default ClickAwayListener;
